import { memo, useCallback } from 'react';
import { Box, Icon } from '@usealloy/component-library';
import { Position } from 'react-flow-renderer';

import type { OutcomeNode as IOutcomeNode, WorkflowNodeProps } from 'flow/service/graph';
import { selectActionByPublicId, selectNodeState } from 'flow/store/redux/selectors';
import { setHoveredNodeId } from 'flow/store/redux/slice';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { getActionName, getOutcomeBaseColor } from 'flow/service/actions';
import {
  StyledBaseNode,
  StyledLeftHandle,
  StyledNodeText,
  StyledRightHandle,
} from '~/dashboard/views/workflows/editor/components/Graph/nodes/BaseNode/BaseNode.styles';
import { formatColor } from '~/dashboard/views/workflows/editor/components/helpers';
import {
  getOutcomeIconName,
  getOutcomeStateStyle,
} from '~/dashboard/views/workflows/editor/components/Island/OutcomeIsland/OnIslandOutcomeNode/helpers';

/**
 * Visual outcome node for embedded workflows in the journey canvas.
 * Renders as a regular flow node (not in an island) with natural positioning by React Flow.
 * Matches the styling of OnIslandOutcomeNode but without the island container's absolute positioning.
 */
function VisualOutcomeNode({ id, type }: WorkflowNodeProps<IOutcomeNode>) {
  const dispatch = useAppDispatch();

  const action = useAppSelector(selectActionByPublicId(id));
  const nodeState = useAppSelector(state => selectNodeState(state, id));

  const handleMouseEnter = useCallback(() => {
    dispatch(setHoveredNodeId(id));
  }, [dispatch, id]);

  const handleMouseLeave = useCallback(() => {
    dispatch(setHoveredNodeId(null));
  }, [dispatch]);

  if (!action || action.type !== 'outcome') {
    return null;
  }

  const {
    outcome: {
      color,
      icon,
      name,
    },
  } = action;

  const outcomeActionName = getActionName(action);
  const baseBackgroundColor = color || getOutcomeBaseColor(outcomeActionName);
  const backgroundColor = formatColor(baseBackgroundColor);
  const outcomeIconName = icon || getOutcomeIconName(outcomeActionName);

  // Use the same state styling as OnIslandOutcomeNode
  const nodeStateStyle = getOutcomeStateStyle({
    outcomeAction: action,
    nodeState,
  });

  return (
    <>
      <StyledLeftHandle type="target" position={Position.Left} />
      <StyledRightHandle type="source" position={Position.Right} />

      <StyledBaseNode
        baseBackgroundColor={backgroundColor}
        nodeState={nodeState}
        style={nodeStateStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid={`workflowNode-${type}-${id}`}
        data-value={name}
        data-dd-action-name={`embedded-outcome-${name}`}
      >
        <Box gap={1} alignItems="center" justifyContent="flex-start">
          <Icon
            icon={outcomeIconName}
            style={{ color: nodeStateStyle.color }}
          />
          <StyledNodeText nodeState={nodeState}>{name}</StyledNodeText>
        </Box>
      </StyledBaseNode>
    </>
  );
}

export default memo(VisualOutcomeNode);
